import {bedrockApi, api} from './api';
import { privateLambdaService } from './privateLambdaService';
import { 
  AIAnalysisRequest, 
  AIAnalysisResponse, 
  GenerateJiraStoriesResponse, 
  Diagram,
  ImageInput,
  GenerateRequirementsResponse
} from '../types';

/**
 * Service for managing Bedrock/AI-related API operations
 */
export const bedrockService = {
  /**
   * Analyze requirements using AWS Bedrock AI models
   * @param {AIAnalysisRequest} analysisRequest - Analysis request data
   * @returns {Promise<AIAnalysisResponse>} AI analysis results
   */
  analyzeRequirements: async (analysisRequest: AIAnalysisRequest): Promise<AIAnalysisResponse> => {
    try {
      return await privateLambdaService.analyzeRequirements(analysisRequest);
    } catch (error: any) {
      console.error('Error analyzing requirements:', {
        name: error?.name,
        code: error?.code,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        message: String(error?.message || 'Unknown').substring(0, 200)
      });
      throw error; // Re-throw to propagate backend error messages
      
      // FALLBACK TO PUBLIC API DISABLED - Backend error messages should be shown to users
      // const response = await bedrockApi.post('/bedrock/analyze', analysisRequest);
      // return response.data;
    }
  },

  /**
   * Generate requirement summaries using Bedrock
   * @param {string} projectId - Project ID
   * @returns {Promise<AIAnalysisResponse>} Generated summaries
   */
  generateSummary: async (projectId: string): Promise<AIAnalysisResponse> => {
    try {
      return await privateLambdaService.generateSummary(projectId);
    } catch (error: any) {
      console.error('Error generating summary:', {
        name: error?.name,
        code: error?.code,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        message: String(error?.message || 'Unknown').substring(0, 200)
      });
      throw error; // Re-throw to propagate backend error messages
      
      // FALLBACK TO PUBLIC API DISABLED - Backend error messages should be shown to users
      // const response = await bedrockApi.post(`/bedrock/projects/${projectId}/summary`);
      // return response.data;
    }
  },

  /**
   * Check for inconsistencies in requirements
   * @param {string} projectId - Project ID
   * @returns {Promise<AIAnalysisResponse>} Consistency check results
   */
  checkConsistency: async (projectId: string): Promise<AIAnalysisResponse> => {
    try {
      return await privateLambdaService.checkConsistency(projectId);
    } catch (error: any) {
      console.error('Error checking consistency:', {
        name: error?.name,
        code: error?.code,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        message: String(error?.message || 'Unknown').substring(0, 200)
      });
      throw error; // Re-throw to propagate backend error messages
      
      // FALLBACK TO PUBLIC API DISABLED - Backend error messages should be shown to users
      // const response = await bedrockApi.post(`/bedrock/projects/${projectId}/consistency`);
      // return response.data;
    }
  },

  /**
   * Generate test cases based on requirements
   * @param {string} projectId - Project ID
   * @param {string} requirementId - Optional requirement ID to focus on specific requirement
   * @returns {Promise<AIAnalysisResponse>} Generated test cases
   */
  generateTestCases: async (projectId: string, requirementId?: string): Promise<AIAnalysisResponse> => {
    try {
      return await privateLambdaService.generateTestCases(projectId, requirementId);
    } catch (error: any) {
      console.error('Error generating test cases:', {
        name: error?.name,
        code: error?.code,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        message: String(error?.message || 'Unknown').substring(0, 200)
      });
      throw error; // Re-throw to propagate backend error messages
      
      // FALLBACK TO PUBLIC API DISABLED - Backend error messages should be shown to users
      // const url = requirementId 
      //   ? `/bedrock/projects/${projectId}/requirements/${requirementId}/test-cases`
      //   : `/bedrock/projects/${projectId}/test-cases`;
      // const response = await bedrockApi.post(url);
      // return response.data;
    }
  },

  generateJiraStories: async (projectId: string): Promise<GenerateJiraStoriesResponse> => {
    try {
      return await privateLambdaService.generateJiraStoriesV2(projectId);
    } catch (error: any) {
      console.error('Error generating Jira stories:', {
        name: error?.name,
        code: error?.code,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        message: String(error?.message || 'Unknown').substring(0, 200)
      });
      throw error; // Re-throw to propagate backend error messages
      
      // FALLBACK TO PUBLIC API DISABLED - Backend error messages should be shown to users
      // const response = await bedrockApi.post(`/bedrock/generate-stories`, {
      //   requirementsDoc,
      //   projectId,
      // });
      // return response.data;
    }
  },

  generateDiagrams: async (projectId: string) => {
    try {
      return await privateLambdaService.generateDiagrams(projectId);
    } catch (error: any) {
      console.error('Error generating diagrams:', {
        name: error?.name,
        code: error?.code,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        message: String(error?.message || 'Unknown').substring(0, 200)
      });
      throw error; // Re-throw to propagate backend error messages
      
      // FALLBACK TO PUBLIC API DISABLED - Backend error messages should be shown to users
      // const response = await bedrockApi.post('/bedrock/generate-diagrams', {
      //   requirementsDoc,
      //   projectId,
      // });
      // return response.data;
    }
  },

  generateFullstackCode: async (projectId: string, techStack: string, inputType?: string) => {
    try {
      return await privateLambdaService.generateFullstackCode(projectId, techStack, inputType);
    } catch (error: any) {
      console.error('Error generating fullstack code:', {
        name: error?.name,
        code: error?.code,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        message: String(error?.message || 'Unknown').substring(0, 200)
      });
      throw error; // Re-throw to propagate backend error messages
      
      // FALLBACK TO PUBLIC API DISABLED - Backend error messages should be shown to users
      // const response = await bedrockApi.post('/bedrock/generate-fullstack', {
      //   diagrams,
      //   projectId,
      //   requirementsDoc,
      //   techStack,
      //   inputType
      // });
      // return response.data;
    }
  },

  editFullstackCode: async (projectId: string, userPrompt: string, syncMode?: boolean) => {
    try {
      return await privateLambdaService.editFullstackCode(projectId, userPrompt, syncMode);
    } catch (error: any) {
      console.error('Error editing fullstack code:', {
        name: error?.name,
        code: error?.code,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        message: String(error?.message || 'Unknown').substring(0, 200)
      });
      throw error; // Re-throw to propagate backend error messages
      
      // FALLBACK TO PUBLIC API DISABLED - Backend error messages should be shown to users
      // const response = await bedrockApi.post('/edit-artifacts/edit-fullstack-code', {
      //   projectId,
      //   userPrompt
      // });
      // return response.data;
    }
  },

  generateFullstackTestCases: async (projectId: string) => {
    try {
      return await privateLambdaService.generateFullstackTestCases(projectId);
    } catch (error: any) {
      console.error('Error generating fullstack test cases:', {
        name: error?.name,
        code: error?.code,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        message: String(error?.message || 'Unknown').substring(0, 200)
      });
      throw error; // Re-throw to propagate backend error messages
      
      // FALLBACK TO PUBLIC API DISABLED - Backend error messages should be shown to users
      // const response = await bedrockApi.post('/bedrock/generate-testcases', {
      //   projectId
      // });
      // return response.data;
    }
  },

  continueFullstackGeneration: async (projectId: string) => {
    try {
      return await privateLambdaService.continueFullstackGeneration(projectId);
    } catch (error) {
      console.error('Error continuing fullstack generation:', error);
      throw error;
    }
  },

  generateFrontendCode: async (requirementsDoc: string, diagrams: string, projectId: string, techStack: string, inputType?: string) => {
    try {
      return await privateLambdaService.generateFrontendCode(projectId, requirementsDoc, diagrams, techStack, inputType);
    } catch (error: any) {
      console.error('Error generating frontend code:', {
        name: error?.name,
        code: error?.code,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        message: String(error?.message || 'Unknown').substring(0, 200)
      });
      throw error; // Re-throw to propagate backend error messages
      
      // FALLBACK TO PUBLIC API DISABLED - Backend error messages should be shown to users
      // const response = await bedrockApi.post('/bedrock/generate-frontend', {
      //   requirementsDoc,
      //   diagrams,
      //   projectId,
      //   techStack,
      //   inputType
      // });
      // return response.data;
    }
  },
  
  generateBackendCode: async (requirementsDoc: string, diagrams: string, projectId: string) => {
    try {
      return await privateLambdaService.generateBackendCode(projectId, requirementsDoc, diagrams);
    } catch (error: any) {
      console.error('Error generating backend code:', {
        name: error?.name,
        code: error?.code,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        message: String(error?.message || 'Unknown').substring(0, 200)
      });
      throw error; // Re-throw to propagate backend error messages
      
      // FALLBACK TO PUBLIC API DISABLED - Backend error messages should be shown to users
      // const response = await bedrockApi.post('/bedrock/generate-backend', {
      //   requirementsDoc,
      //   diagrams,
      //   projectId,
      // });
      // return response.data;
    }
  },
  
  generateDatabaseSchema: async (projectId: string) => {
    try {
      return await privateLambdaService.generateDatabaseSchema(projectId);
    } catch (error) {
      console.error('Error generating database schema:', error);
      throw error; // Re-throw to propagate backend error messages
      
      // FALLBACK TO PUBLIC API DISABLED - Backend error messages should be shown to users
      // const response = await bedrockApi.post('/bedrock/generate-database', {
      //   requirementsDoc,
      //   projectId,
      // });
      // return response.data;
    }
  },

  /**
   * Edit generated database schema based on user prompt
   */
  editDatabaseSchema: async (projectId: string, userPrompt: string, syncMode?: boolean) => {
    try {
      return await privateLambdaService.editDatabaseSchema(projectId, userPrompt, syncMode);
    } catch (error: any) {
      console.error('Error editing database schema via private service:', error);
      throw error; // Re-throw to propagate backend error messages
      
      // FALLBACK TO PUBLIC API DISABLED - Backend error messages should be shown to users
      // Note: Previous retry logic with session refresh has been disabled
      // If authentication errors occur, the backend will return user-friendly messages
      // if (error.message?.includes('expired') || error.message?.includes('token') || 
      //     error.response?.status === 401 || error.response?.status === 403) {
      //   console.log('Authentication issue detected, retrying with refreshed session...');
      //   try {
      //     const { Auth } = await import('aws-amplify');
      //     await Auth.currentSession();
      //     return await privateLambdaService.editDatabaseSchema(projectId, userPrompt);
      //   } catch (retryError) {
      //     console.error('Retry with refreshed session failed:', retryError);
      //   }
      // }
      // console.log('Falling back to public API...');
      // const response = await api.post('/edit-artifacts/edit-database-schema', {
      //   projectId,
      //   userPrompt
      // });
      // return response.data;
    }
  },

  /**
   * Edit diagrams based on user prompt
   */
  editDiagrams: async (projectId: string, userPrompt: string, diagramType: string, syncMode?: boolean) => {
    try {
      return await privateLambdaService.editDiagrams(projectId, userPrompt, diagramType, syncMode);
    } catch (error: any) {
      console.error('Error editing diagrams via private service:', error);
      throw error; // Re-throw to propagate backend error messages
    }
  },

  /**
   * Edit JIRA stories based on user prompt
   */
  editJiraStories: async (projectId: string, userPrompt: string, syncMode?: boolean) => {
    try {
      return await privateLambdaService.editJiraStories(projectId, userPrompt, syncMode);
    } catch (error: any) {
      console.error('Error editing JIRA stories via private service:', error);
      throw error; // Re-throw to propagate backend error messages
    }
  },

  /**
   * Append a new requirements section via LLM
   */
  editRequirements: async (projectId: string, userPrompt: string) => {
    try {
      return await privateLambdaService.editRequirements(projectId, userPrompt);
    } catch (error: any) {
      console.error('Error editing requirements:', error);
      throw error;
    }
  },

  generateInfrastructure: async (projectId: string) => {
    try {
      return await privateLambdaService.generateInfrastructure(projectId);
    } catch (error) {
      console.error('Error generating infrastructure:', error);
      throw error; // Re-throw to propagate backend error messages
      
      // FALLBACK TO PUBLIC API DISABLED - Backend error messages should be shown to users
      // const response = await bedrockApi.post('/bedrock/generate-terraform', {
      //   requirementsDoc,
      //   projectId,
      // });
      // return response.data;
    }
  },

  /**
   * Regenerate content for review functionality using private Lambda
   */
  regenerateContent: async (stage: string, feedback: string, attachments?: any[]) => {
    try {
      // Use private Lambda service for secure invocation
      return await privateLambdaService.regenerateContent(stage, feedback, attachments);
    } catch (error) {
      console.error('Error regenerating content:', error);
      throw error;
    }
  },

  /**
   * Generate design document
   * @param {string} projectId - Project ID
   * @returns {Promise<any>} Design document generation response
   */
  generateDesignDocument: async (projectId: string): Promise<any> => {
    try {
      return await privateLambdaService.generateDesignDocument(projectId);
    } catch (error) {
      console.error('Error generating design document:', error);
      throw error; // Re-throw to propagate backend error messages
      
      // FALLBACK TO PUBLIC API DISABLED - Backend error messages should be shown to users
      // const response = await bedrockApi.post(`/bedrock/generate-design-document/${projectId}`);
      // return response.data;
    }
  },

  /**
   * Upload design document to Confluence
   * @param {string} projectId - Project ID
   * @returns {Promise<any>} Confluence upload response
   */
  uploadToConfluence: async (projectId: string): Promise<any> => {
    try {
      return await privateLambdaService.uploadDesignDocumentToConfluence(projectId);
    } catch (error) {
      console.error('Error uploading to Confluence:', error);
      throw error; // Re-throw to propagate backend error messages
      
      // FALLBACK TO PUBLIC API DISABLED - Backend error messages should be shown to users
      // const response = await bedrockApi.post(`/confluence/upload-design-document/${projectId}`);
      // return response.data;
    }
  },

  /**
   * Generate requirements document from raw text and optional images
   * @param {string} rawText - Text description of requirements
   * @param {string} projectId - Project ID (optional)
   * @param {ImageInput[]} images - Array of image objects with base64Data and mediaType (optional)
   * @returns {Promise<GenerateRequirementsResponse>} Generated requirements document
   */
  generateRequirementsDoc: async (
    rawText: string, 
    projectId?: string,
    images?: ImageInput[]
  ): Promise<GenerateRequirementsResponse> => {
    try {
      return await privateLambdaService.generateRequirementsDoc(rawText, projectId, images);
    } catch (error) {
      console.error('Error generating requirements document:', error);
      throw error; // Re-throw to propagate backend error messages
      
      // FALLBACK TO PUBLIC API DISABLED - Backend error messages should be shown to users
      // const response = await bedrockApi.post('/bedrock/generate-requirements', {
      //   rawText,
      //   projectId,
      //   images
      // });
      // return response.data;
    }
  }
};

export default bedrockService;